import type { Board } from "./board";
import type { Direction } from "./direction";
import { directionDelta } from "./direction";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "./effects/movementEffect";
import type { Mob } from "./mob";
import type { PhysicalThingWithHp } from "./physicalThingWithHp";
import { Projectile } from "./projectile";
import type { Thing } from "./thing";
import { BigSquid } from "./mobs/bigSquid";
import { BigSquidTentacle } from "./mobs/bigSquidTentacle";
import { Chasm } from "./things/chasm";
import { Landmine } from "./things/landmine";
import { LavaPool } from "./things/lavaPool";
import type { XY } from "./xy";

export interface PushResult {
	moved: boolean;
	tilesPushed: number;
	hitWall: boolean;
	landedOnHazard: boolean;
	landedOnFlyable: boolean;
	origin: XY;
	destination: XY;
}

function hasHazard(board: Board, xy: XY): boolean {
	return board.getThingsAt(xy).some((t) => t.active && (t instanceof Projectile || t instanceof Landmine));
}

function isFlyable(board: Board, xy: XY): boolean {
	if (board.isPassable(xy)) return false;
	return board.getThingsAt(xy).some((t) => t instanceof Chasm || t instanceof LavaPool);
}

export function isPushImmune(target: PhysicalThingWithHp): boolean {
	return target instanceof BigSquid || target instanceof BigSquidTentacle;
}

export function pushMob(
	board: Board,
	target: Mob,
	direction: Direction,
	distance: number,
	author: Thing,
): PushResult {
	const delta = directionDelta(direction);
	const origin = target.xy;
	let dest = origin;
	let tilesPushed = 0;
	let hitWall = false;
	let landedOnHazard = false;
	for (let i = 0; i < distance; i++) {
		const next = dest.add(delta);
		if (!board.isPassable(next)) {
			if (board.isInBounds(next) && isFlyable(board, next)) {
				dest = next;
				tilesPushed++;
				continue;
			}
			hitWall = board.isInBounds(next);
			break;
		}
		if (board.hasMob(next)) {
			break;
		}
		if (hasHazard(board, next)) {
			dest = next;
			tilesPushed++;
			landedOnHazard = true;
			break;
		}
		dest = next;
		tilesPushed++;
	}
	const moved = !dest.equals(origin);
	if (moved) {
		board.moveThing(target, dest);
		const time = Math.max(STANDARD_MOVEMENT_TIME, tilesPushed * (STANDARD_MOVEMENT_TIME / 2));
		board.addEffect(new MovementEffect(target, origin, dest, time));
		for (const thing of board.getThingsAt(dest)) {
			if (thing instanceof Projectile && thing.active && thing !== author) {
				thing.checkForTarget();
			}
		}
	}
	return {
		moved,
		tilesPushed,
		hitWall,
		landedOnHazard,
		landedOnFlyable: moved && !target.isFlying() && isFlyable(board, dest),
		origin,
		destination: dest,
	};
}
